import { useEffect } from 'react';
import { Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { Center, Loader } from '@mantine/core';
import { useAccountPermissions, type AccountPermissions } from '../useAccountPermissions';

/**
 * Maps route path prefixes to their required permission key.
 * Child routes (e.g., /account/team/new) inherit from their parent section.
 */
const ROUTE_PERMISSION_MAP: [string, keyof AccountPermissions][] = [
  ['/account/billing', 'canViewBilling'],
  ['/account/usage', 'canViewUsage'],
  ['/account/upgrade-trial', 'canViewTrialUpgrade'],
  ['/account/team', 'canViewTeam'],
  ['/account/sub-accounts', 'canViewSubAccounts'],
  ['/account/upload-profiles', 'canViewUploadProfiles'],
];

function getRequiredPermission(path: string): keyof AccountPermissions | null {
  for (const [prefix, permKey] of ROUTE_PERMISSION_MAP) {
    if (path === prefix || path.startsWith(prefix + '/')) {
      return permKey;
    }
  }
  return null;
}

/**
 * Layout route for /account — handles:
 * 1. Loading state while auth data resolves
 * 2. Permission-based route guarding for all child routes
 * 3. Bare /account redirect to first allowed child
 */
export function AccountLayout() {
  const navigate = useNavigate();
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;
  const perms = useAccountPermissions();

  useEffect(() => {
    // Don't redirect while permissions are still loading
    if (perms.isLoading) return;

    const isBareAccountPath = currentPath === '/account' || currentPath === '/account/';

    if (isBareAccountPath) {
      // Redirect bare /account to first allowed child route
      if (perms.firstAllowedRoute) {
        navigate({ to: perms.firstAllowedRoute, replace: true });
      } else {
        navigate({ to: '/', replace: true });
      }
      return;
    }

    // Check if user has permission for the current child route
    const requiredPerm = getRequiredPermission(currentPath);
    if (requiredPerm && !perms[requiredPerm]) {
      // User doesn't have access — redirect to first allowed route or dashboard
      if (perms.firstAllowedRoute) {
        navigate({ to: perms.firstAllowedRoute, replace: true });
      } else {
        navigate({ to: '/', replace: true });
      }
    }
  }, [currentPath, perms, navigate]);

  // Show loader while auth data is resolving to prevent flash of empty content
  if (perms.isLoading) {
    return (
      <Center h="50vh">
        <Loader size="md" />
      </Center>
    );
  }

  return <Outlet />;
}
